import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  BulbOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  InboxOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  UploadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Alert, Button, Card, Layout, Progress, Space, Spin, Typography, Upload, message } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import AppLayout from "@/Components/Common/AppLayout";
import { LoadingContext } from "@/Context/LoadingContext";
import { UserContext } from "@/Context/UserContext";

const { Dragger } = Upload;
const { Title, Text } = Typography;
const { Content } = Layout;

// Enhanced Loading animation component with better UI and branding
const LoadingAnimation = ({ stage }) => {
  const stages = [
    {
      icon: <UploadOutlined />,
      text: "Uploading your resume...",
      description: "Securely uploading your file",
      color: "#1890ff",
      progress: 0,
    },
    {
      icon: <FileTextOutlined />,
      text: "Reading your resume...",
      description: "Processing your document",
      color: "#1890ff",
      progress: 25,
    },
    {
      icon: <ThunderboltOutlined />,
      text: "Analyzing with AI...",
      description: "AI engine is working",
      color: "#1890ff",
      progress: 50,
    },
    {
      icon: <BulbOutlined />,
      text: "Extracting information...",
      description: "Parsing resume sections",
      color: "#1890ff",
      progress: 75,
    },
    {
      icon: <TrophyOutlined />,
      text: "Preparing interface...",
      description: "Setting up your workspace",
      color: "#1890ff",
      progress: 100,
    },
  ];

  const currentStage = stages[Math.min(stage, stages.length - 1)];

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: "40px 20px",
      }}
    >
      {/* Main loading card */}
      <Card
        style={{
          width: "100%",
          maxWidth: "500px",
          textAlign: "center",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        {/* Progress header */}
        <div style={{ marginBottom: "32px" }}>
          <div
            style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              fontSize: "32px",
              color: "white",
            }}
          >
            {currentStage.icon}
          </div>
          <Title level={2} style={{ margin: 0, color: "#333", textAlign: "center" }}>
            {currentStage.text}
          </Title>
          <Text
            style={{ color: "#666", fontSize: "16px", textAlign: "center", display: "block", marginTop: "8px" }}
          >
            {currentStage.description}
          </Text>
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: "32px" }}>
          <Progress
            percent={currentStage.progress}
            strokeColor={{
              "0%": "#1890ff",
              "100%": "#40a9ff",
            }}
            trailColor="#f0f2f5"
            strokeWidth={8}
            showInfo={false}
            style={{ marginBottom: "8px" }}
          />
          <Text style={{ color: "#666", fontSize: "14px", textAlign: "center", display: "block" }}>
            {currentStage.progress}% Complete
          </Text>
        </div>

        {/* Steps indicator */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            padding: "0 20px",
          }}
        >
          {stages.map((item, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: 1,
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  background: index <= stage ? "#1890ff" : "#f0f2f5",
                  color: index <= stage ? "white" : "#bfbfbf",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  marginBottom: "8px",
                  border: index === stage ? "2px solid #40a9ff" : "none",
                  transition: "all 0.3s ease",
                }}
              >
                {index < stage ? <CheckCircleOutlined /> : item.icon}
              </div>
              <Text
                style={{
                  fontSize: "10px",
                  color: index <= stage ? "#333" : "#bfbfbf",
                  textAlign: "center",
                  lineHeight: "1.2",
                }}
              >
                {item.text.split(" ")[0]}
              </Text>
            </div>
          ))}
        </div>

        {/* Animated elements */}
        <div style={{ textAlign: "center" }}>
          <Spin size="large" style={{ color: "#1890ff", marginBottom: "16px" }} />
          <Text style={{ color: "#999", fontSize: "14px", display: "block" }}>
            Please wait while we process your resume... This can take up to 1 minute.
          </Text>
        </div>
      </Card>
    </div>
  );
};

const ResumeUpload = () => {
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { startLoading, stopLoading } = useContext(LoadingContext);
  const { userProfileData, updateProfileInfo } = useContext(UserContext);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState(0);
  const [planInfo, setPlanInfo] = useState({
    plan: "free",
    maxResumes: 3,
    currentResumeCount: 0,
    canCreateResume: true,
  });
  const [checkingPlan, setCheckingPlan] = useState(true);

  // Auth guard - redirect to login if not authenticated
  useEffect(() => {
    const authToken = localStorage.getItem("authorization");
    const userId = localStorage.getItem("userId");
    if (!authToken || !userId) {
      navigate("/app/login?returnUrl=" + encodeURIComponent(window.location.pathname));
    }
  }, [navigate]);

  // Check for existing resume ID from user profile and auto-redirect
  useEffect(() => {
    if (userProfileData?.resumeId) {
      // Auto-redirect immediately to existing resume
      navigate(`/app/resume/${userProfileData.resumeId}`);
    }
  }, [navigate, userProfileData]);

  // Fetch plan info to check if user can create resume
  useEffect(() => {
    const checkPlanInfo = async () => {
      try {
        setCheckingPlan(true);
        const userId = localStorage.getItem("userId");
        if (!userId) return;

        const response = await fetch.get(`/user/dashboard/${userId}`, false);
        if (response.success && response.data.planInfo) {
          setPlanInfo(response.data.planInfo);
          // If user can't create resume, redirect to dashboard
          if (!response.data.planInfo.canCreateResume) {
            message.warning("You have reached your resume limit.");
            navigate("/app/dashboard");
          }
        }
      } catch (error) {
        console.error("Error checking plan info:", error);
      } finally {
        setCheckingPlan(false);
      }
    };
    checkPlanInfo();
  }, []);

  // Simulate loading stages progression
  useEffect(() => {
    if (loading) {
      const interval = setInterval(() => {
        setLoadingStage((prev) => {
          if (prev >= 4) {
            clearInterval(interval);
            return prev;
          }
          return prev + 1;
        });
      }, 3000); // 3 seconds per stage
      return () => clearInterval(interval);
    } else {
      setLoadingStage(0);
    }
  }, [loading]);

  const validFileTypes = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  const maxFileSize = 2 * 1024 * 1024; // 2MB in bytes

  const handleDropZoneDrop = useCallback(
    (_dropFiles, acceptedFiles) => {
      const newFile = acceptedFiles[0];

      if (!newFile) return;

      // Validate file type
      if (!validFileTypes.includes(newFile.type)) {
        setError("Please upload only PDF or DOCX files.");
        return;
      }

      // Validate file size
      if (newFile.size > maxFileSize) {
        setError("File size must not exceed 2MB.");
        return;
      }

      setFile(newFile);
      setError("");
      // Auto-start processing when file is selected
      handleUploadAndParse(newFile);
    },
    [validFileTypes, maxFileSize]
  );

  const removeFile = () => {
    setFile(null);
    setError("");
  };

  const handleUploadAndParse = async (fileToUpload = file) => {
    if (!fileToUpload) return;

    // Double-check plan limit before upload
    if (!planInfo.canCreateResume) {
      setError("You have reached your resume limit. Free plan allows only 3 resumes.");
      return;
    }

    setError("");
    setLoading(true);
    setLoadingStage(0);

    try {
      // Convert file to base64 to work with Axios JSON requests
      const fileDataPromise = new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]); // Remove data:mime;base64, prefix
        reader.readAsDataURL(fileToUpload);
      });

      const fileData = await fileDataPromise;

      // Upload file using base64 approach that works with Axios (disable loading for this call)
      const uploadResponse = await fetch.post(
        "/upload-base64-file",
        {
          fileData: fileData,
          fileName: fileToUpload.name,
          fileType: fileToUpload.type,
          fileSize: fileToUpload.size,
        },
        false
      ); // Pass false to disable automatic loading

      if (!uploadResponse.success) {
        throw new Error("Upload failed");
      }

      const uploadData = uploadResponse;

      // Call AI parsing endpoint (disable loading for this call)
      const userId = localStorage.getItem("userId");
      const parseResponse = await fetch.post(
        "/parse-resume",
        {
          fileUrl: uploadData.data.assetUrl,
          fileName: uploadData.data.assetName,
          fileSize: fileToUpload.size,
          fileType: fileToUpload.type,
          userId: userId, // Include userId if logged in
          resumeSource: "upload",
        },
        false
      ); // Pass false to disable automatic loading

      const parsedResult = parseResponse;

      // Resume ID is now linked to user in backend, update user profile
      if (parsedResult.data?.resumeId) {
        // Update user profile with new resumeId (backend already links it)
        updateProfileInfo({ ...userProfileData, resumeId: parsedResult.data.resumeId });

        // Stop loading
        setLoading(false);

        if (parseResponse.success) {
          message.success("Resume processed successfully!");
        } else {
          // Parsing failed but we have a resume ID to show failed record
          message.warning("Resume parsing failed, but you can still access your upload.");
        }

        // Navigate directly to resume view
        navigate(`/app/resume/${parsedResult.data.resumeId}`);
      } else {
        console.error("No resume ID found in response", parsedResult);
        throw new Error("AI parsing failed. Please try again.");
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || "An error occurred during processing. Please try again.");
    }
  };

  // Render loading state while checking plan
  if (checkingPlan) {
    return (
      <AppLayout>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  // Render loading state during upload
  if (loading) {
    return (
      <AppLayout>
        <div
          style={{
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100vh",
            overflowY: "auto",
            width: "100%",
          }}
        >
          <LoadingAnimation stage={loadingStage} />
        </div>
      </AppLayout>
    );
  }

  const handleBack = () => {
    navigate("/app/resume-choice");
  };

  return (
    <AppLayout>
      <style>{`
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          box-sizing: border-box !important;
          height: 100% !important;
          width: 100% !important;
          overflow-x: clip !important;
          overflow-y: auto !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }
        *, *::before, *::after { box-sizing: border-box !important; }
        .ant-layout, .ant-layout-content { margin: 0 !important; padding: 0 !important; position: relative !important; }
        .ant-layout { overflow: visible !important; }
        body { user-select: none !important; -webkit-user-select: none !important; -moz-user-select: none !important; -ms-user-select: none !important; }

        /* Mobile Responsive Styles */
        @media (max-width: 768px) {
          .back-button-container {
            left: 60px !important;
            top: 12px !important;
          }

          .back-to-options-btn {
            height: 32px !important;
            paddingLeft: 8px !important;
            paddingRight: 8px !important;
            fontSize: 12px !important;
          }

          .back-button-text {
            display: none;
          }
        }
      `}</style>
      <div
        style={{
          padding: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          height: "100vh",
          overflowY: "auto",
          width: "100%",
          position: "relative",
        }}
      >
        {/* Back button - positioned at top left corner */}
        <div style={{
          position: "absolute",
          top: "20px",
          left: "80px",
          zIndex: 10
        }}>
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="back-to-options-btn"
            style={{
              fontSize: "14px",
              borderColor: "#1890ff",
              color: "#1890ff",
              backgroundColor: "transparent",
              height: "36px",
              paddingLeft: "16px",
              paddingRight: "16px",
              borderRadius: "6px",
              fontWeight: "500",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              transition: "all 0.3s ease",
            }}
          >
            <span className="back-button-text">Back to options</span>
          </Button>
        </div>

        <div style={{ marginTop: "24px", marginBottom: "32px", textAlign: "center", width: "100%", padding: "0 16px" }}>
          <Title level={2} style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)" }}>📄 Enhance Existing Resume</Title>
          <Text type="secondary" style={{ fontSize: "clamp(0.875rem, 3vw, 1rem)" }}>Upload your resume for AI-powered parsing and analysis</Text>
        </div>

        <div
          className="resume-upload-container"
          style={{
            maxWidth: "500px",
            width: "100%",
            padding: "0 16px",
          }}
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {error && (
              <Alert message="Error" description={error} type="error" closable onClose={() => setError("")} />
            )}

            <Card style={{ textAlign: "center" }}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Title level={4}>Upload your resume</Title>
                <Text type="secondary">Supported formats: PDF, DOCX (Max size: 2MB)</Text>

                <Dragger
                  accept=".pdf,.docx"
                  beforeUpload={() => false}
                  onChange={(info) => {
                    const { fileList } = info;
                    if (fileList.length > 0) {
                      const newFile = fileList[0].originFileObj;
                      handleDropZoneDrop([], [newFile]);
                    }
                  }}
                  fileList={[]}
                  disabled={false}
                  showUploadList={false}
                  style={{ padding: "20px" }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Add file</p>
                  <p className="ant-upload-hint">or drop file to upload</p>
                </Dragger>
              </Space>
            </Card>
          </Space>
        </div>
      </div>
    </AppLayout>
  );
};

export default ResumeUpload;
